<template>
  <div
    v-if="estimationsToShow.isAssistanceUnavailable && estimationsToShow.estimations.length > 0"
    class="text-center font-italic pt-10"
  >
    Lyft Assisted is unavailable. Book Uber for elbow assist instead
  </div>

  <div
    v-if="estimationsToShow.estimations.length > 0"
    class="estimation-card-container"
  >
    <div
      v-for="({level, ...estimatesByVendor}, index) in estimationsToShow.estimations"
      :key="level"
    >
      <div
        v-if="level !== RideLevel.other"
        class="level-name"
        :class="{'mt-4': index !== 0}"
      >
        {{ level }}
      </div>
      <div
        v-for="vendor in vendors"
        :key="vendor"
        class="level-row"
      >
        <EstimationRideCard
          v-for="rideEstimate in estimatesByVendor[vendor]"
          :key="rideEstimate.rideType"
          :rideEstimate="rideEstimate"
          :isSelected="vendor === 'nash' ? rideEstimate.providerName === estimationRide?.providerName : rideEstimate.rideType === estimationRide?.rideType"
          :isPriceWinner="winners.priceWinners.includes(rideEstimate)"
          :isEtaWinner="winners.etaWinners.includes(rideEstimate)"
          :isOverallWinner="winners.overallWinners.includes(rideEstimate)"
          :isLyftUnavailable="isRideOutsideUS && vendor === 'lyft'"
          @click="onSelectEstimate(rideEstimate)"
        />
      </div>
    </div>

    <template v-if="otherRideEstimates.length > 0">
      <div class="level-name mt-4">
        Other
      </div>
      <div class="level-row">
        <EstimationRideCard
          v-for="rideEstimate in otherRideEstimates"
          :key="rideEstimate.rideType"
          :rideEstimate="rideEstimate"
          :isSelected="rideEstimate.rideType === estimationRide?.rideType"
          :isPriceWinner="winners.priceWinners.includes(rideEstimate)"
          :isEtaWinner="winners.etaWinners.includes(rideEstimate)"
          :isOverallWinner="winners.overallWinners.includes(rideEstimate)"
          @click="onSelectEstimate(rideEstimate)"
        />
      </div>
    </template>
  </div>

  <div
    v-else
    class="text-center font-italic pt-10 pb-10"
  >
    No ride options to show
    <template v-if="selectedRideLevelAndVendor?.level">
      for rideType <b>{{ selectedRideLevelAndVendor.level }}</b>
    </template>
    <template v-if="selectedRideLevelAndVendor?.vendor">
      from <b>{{ selectedRideLevelAndVendor.vendor }}</b>
    </template>
  </div>

  <div class="text-center">
    <div
      v-if="estimationsToShow.isThereMoreToShow && !isShowingAllRides"
      link
      class="see-all-estimations mt-3"
      @click="isShowingAllRides = true"
    >
      See all Ride Types
    </div>
    <div v-if="!isNashDelivery" class="mt-2 pb-20 d-flex justify-content-center">
      <el-checkbox
        v-model="customerWantsLyft"
        :disabled="!estimationsToShow.isLyftAvailableAndHidden"
      >
        Customer asked for Lyft
      </el-checkbox>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import _ from 'lodash';

import { RideLevel, VendorName, RideType } from '@/models/rideType';
import EstimationRideCard from './EstimationRideCard.vue';
import { RideEstimate } from '@/models/ride';
import { rideLevelOrder } from '@/constants/ride';

const vendors = ['uber', 'lyft', 'nash'] as const;

const emit = defineEmits(['select-estimate', 'default-estimate']);

const props = withDefaults(
  defineProps<{
    estimationList: RideEstimate[],
    estimationRide: RideEstimate | null,
    typeOfCar: RideLevel,
    isRideOutsideUS: boolean,
    isInLyftAssistedTestGroup?: boolean,
    isNashDelivery?: boolean,
  }>(),
  {
    isNashDelivery: false,
  },
);

const isShowingAllRides = ref(false);
const customerWantsLyft = ref(false);

const selectedRideLevelAndVendor = computed(() => getRideLevelAndVendor(props.typeOfCar));
const estimationsToShow = computed(() => generateMultipleEstimationList({
  allRideEstimates: props.estimationList,
  selectedRideLevelAndVendor: selectedRideLevelAndVendor.value,
}));
const winners = computed(() => getEstimationWinner(props.estimationList));

const getRideLevelAndVendor = (typeOfCar: RideLevel) => {
  if (rideLevelOrder.includes(typeOfCar)) {
    return {level: typeOfCar, vendor: null};
  }

  const rideType = RideType.findByKey(typeOfCar ?? '');
  if (!rideType) {
    return null;
  }

  return {level: rideType.level, vendor: rideType.vendor};
};

const generateMultipleEstimationList = ({
  allRideEstimates,
  selectedRideLevelAndVendor,
}: {
  allRideEstimates: RideEstimate[];
  selectedRideLevelAndVendor?: {
    level: RideLevel;
    vendor: VendorName | null;
  } | null;
}) => {
  // Depends on: allRideEstimates, isShowingAllRides, customerWantsLyft
  // Returns:
  //   estimations (which estimations to show)
  //   isThereMoreToShow (whether there are estimations hidden due to isShowingAllRides=false)
  //   isLyftAvailableAndHidden (whether there are estimations hidden due to customerWantsLyft=false)

  const estimationsGroupedByLevel = _.groupBy(allRideEstimates, ({ rideType }) => RideType.findByKey(rideType)?.level ?? RideLevel.other) as Record<RideLevel, RideEstimate[]>;

  let isThereMoreToShow = false;
  let isLyftAvailableAndHidden = false;

  // Test-group caller with no Lyft Assisted available: instead of an empty list,
  // fall back to showing the car level (uberX) by default so the operator can still book.
  const isAssistanceUnavailable =
    Boolean(props.isInLyftAssistedTestGroup) &&
    selectedRideLevelAndVendor?.level === RideLevel.assistance &&
    !estimationsGroupedByLevel[RideLevel.assistance]?.length;

  // a non-uber vendor is picked from the dropdown
  const isNonUberSelected = selectedRideLevelAndVendor?.vendor && selectedRideLevelAndVendor.vendor !== VendorName.UBER;

  const estimations = rideLevelOrder.flatMap((level) => {
    const rideEstimations = estimationsGroupedByLevel[level];
    if (!rideEstimations || rideEstimations.length === 0) {
      return [];
    }

    const uber: Array<RideEstimate> = [];
    const lyft: Array<RideEstimate> = [];
    const other: Array<RideEstimate> = [];
    const nash: Array<RideEstimate> = [];

    // there's no uber ride with eta under 15 mins
    const isUberEtaTooLong = !rideEstimations.some(({ rideType, eta }) =>
      RideType.findByKey(rideType)?.vendor === VendorName.UBER && eta != null && eta < 15
    );

    // If the "customer wants lyft" checkbox is checked by the operator
    // there's no uber ride with eta under 15 mins
    // Or there's no uber ride with eta under 15 mins
    const canShowNonUber = customerWantsLyft.value || isNonUberSelected || isUberEtaTooLong;

    for (const estimation of rideEstimations) {
      const rideType = RideType.findByKey(estimation.rideType);
      if (rideType?.vendor === VendorName.NASH) {
        nash.push(estimation);
      } else if (rideType?.vendor === VendorName.UBER) {
        uber.push(estimation);
      } else if (canShowNonUber || estimation.isDefaultOption) {
        if (rideType?.vendor === VendorName.LYFT) {
          lyft.push(estimation);
        } else {
          other.push(estimation);
        }
      }
    }

    if (props.isNashDelivery) {
      return [{ level, uber, lyft, nash, other }];
    }

    const hasLyftEstimations = rideEstimations.some(({ rideType }) => RideType.findByKey(rideType)?.vendor === VendorName.LYFT);
    isLyftAvailableAndHidden = isLyftAvailableAndHidden || !canShowNonUber && hasLyftEstimations;

    const hasDefaultOption = rideEstimations.some((estimation) => estimation.isDefaultOption);
    const isCarLevelFallback = isAssistanceUnavailable && level === RideLevel.car;
    if (selectedRideLevelAndVendor && selectedRideLevelAndVendor.level !== level && (uber.length || lyft.length || nash.length || other.length)) {
      isThereMoreToShow = true;
      if (!isShowingAllRides.value && !hasDefaultOption && !isCarLevelFallback) {
        return [];
      }
    }

    if (uber.length === 0 && lyft.length === 0 && other.length === 0 && nash.length === 0) {
      return [];
    }

    return [{ level, uber, lyft, nash, other }];
  });

  return { estimations, isThereMoreToShow, isLyftAvailableAndHidden, isAssistanceUnavailable };
};

const getEstimationWinner = (estimations: RideEstimate[]) => {
  const allRides = estimations.filter(({eta}) => !_.isNil(eta));
  const minPrice = _.min(allRides.map(({lowEstimate, highEstimate}) => (lowEstimate + highEstimate) / 2));
  const minEta = _.minBy(allRides, ({eta}) => eta)?.etaText;
  const priceWinners = allRides.filter(({lowEstimate, highEstimate}) => (lowEstimate + highEstimate) / 2 === minPrice);
  const etaWinners = allRides.filter(({etaText}) => etaText === minEta);

  return {
    priceWinners,
    etaWinners,
    overallWinners: _.intersection(priceWinners, etaWinners),
  };
};

const otherRideEstimates = computed(() => estimationsToShow.value.estimations.map(item => item.other).flat())

// Estimates the operator can select, in the order their cards are rendered.
const selectableEstimates = computed(() =>
  estimationsToShow.value.estimations
    .flatMap((estimatesByVendor) =>
      vendors.flatMap((vendor) =>
        props.isRideOutsideUS && vendor === 'lyft' ? [] : estimatesByVendor[vendor],
      ),
    )
    .filter(({ eta }) => !_.isNil(eta)),
);

// Emits default estimate
watch(
  selectableEstimates,
  (estimates) => {
    if (estimates.length === 0) {
      return;
    }

    const isSelectedEstimateShown = estimates.some((estimate) => estimate === props.estimationRide);
    if (isSelectedEstimateShown) {
      return;
    }

    emit('default-estimate', estimates[0]);
  },
  { immediate: true },
);

const onSelectEstimate = (rideEstimate: RideEstimate) => {
  emit('select-estimate', rideEstimate);
};
</script>

<style lang="scss" scoped>
.estimation-card-container {
  max-width: 500px;
  margin: 12px auto;
  width: 100%;
  .level-name {
    font-weight: 700;
    text-transform: uppercase;
  }
  .level-row {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
  .estimation-col {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
}
.see-all-estimations {
  text-decoration: underline;
  font-size: 14px;
  cursor: pointer;
  color: #6d509c;

  &:hover {
    color: red;
  }
}
</style>
